import { dispatchAction } from '@ardoq/rxbeach';
import { editViewpoint } from 'viewpointBuilder/actions';
import {
  requestRemoveLoadedState,
  setTraversalContextComponent,
  toggleCollapsedPathActive,
  toggleLoadedStateVisibility,
} from 'loadedState/actions';
import {
  applyFiltersForContextSwitcher as applyFiltersForContextSwitcherAction,
  ApplyFiltersForContextSwitcherPayload,
} from 'viewpointBuilder/selectContextTab/actions';
import { loadedStateOperations } from 'loadedState/loadedStateOperations';
import {
  ArdoqId,
  LoadedState,
  LoadedStateHash,
  LoadedStateType,
  PathCollapsingRule,
  QueryBuilderQuery,
} from '@ardoq/api-types';
import { setStartQueryForLoadedState } from 'loadedState/setStartContextForLoadedState';
import { trackEvent } from 'tracking/tracking';
import { ViewpointNavigatorTrackingEventsNames } from './tracking';
import { resetAllSearchState } from '../search/actions';
import { actionNamespaceViewpointNavigatorContextSwitcher } from '@ardoq/global-consts';

const toggleItemVisibility = (loadedStateHash: LoadedStateHash) => {
  dispatchAction(toggleLoadedStateVisibility({ loadedStateHash }));
};

const removeLoadedState = (payload: {
  loadedStateHash: LoadedStateHash;
  loadedStatesCount: number;
  hasTraversalDefinition: boolean;
  isSaved: boolean;
}) => {
  dispatchAction(requestRemoveLoadedState(payload));
};

const editBlock = (loadedState: LoadedState) => {
  trackEvent(ViewpointNavigatorTrackingEventsNames.CLICKED_EDIT_DATASET_BUTTON);
  const loadedStateHash = loadedStateOperations.stateToHash(loadedState);
  switch (loadedState.type) {
    case LoadedStateType.TRAVERSAL:
      dispatchAction(
        editViewpoint({
          loadedStateHash: loadedStateHash,
          context: 'editSubgraph',
        })
      );
      break;
    case LoadedStateType.CREATED_ITEMS:
      // this should never happen, editing created items is not possible and the button should not be available
      break;
    default:
      loadedState satisfies never;
  }
};

const setContextComponentForBlock = (
  loadedStateHash: LoadedStateHash,
  componentIds: ArdoqId[]
) => {
  dispatchAction(
    setTraversalContextComponent({
      loadedStateHash,
      componentSelection: {
        startSet: componentIds,
      },
    })
  );
};

const resetComponentSearchStream = () => {
  dispatchAction(
    resetAllSearchState(),
    actionNamespaceViewpointNavigatorContextSwitcher
  );
};

const applyFiltersForContextSwitcher = ({
  componentTypeName,
  startSetFilter,
}: ApplyFiltersForContextSwitcherPayload) => {
  dispatchAction(
    applyFiltersForContextSwitcherAction({ startSetFilter, componentTypeName }),
    actionNamespaceViewpointNavigatorContextSwitcher
  );
};

const toggleCollapsedPath = (
  rule: PathCollapsingRule,
  loadedStateHash: LoadedStateHash
) => dispatchAction(toggleCollapsedPathActive({ rule, loadedStateHash }));

export type ViewpointNavigatorCommands = {
  toggleItemVisibility: (loadedStateHash: LoadedStateHash) => void;
  removeLoadedState: ({
    loadedStateHash,
    loadedStatesCount,
    hasTraversalDefinition,
    isSaved,
  }: {
    loadedStateHash: LoadedStateHash;
    loadedStatesCount: number;
    hasTraversalDefinition: boolean;
    isSaved: boolean;
  }) => void;
  editBlock: (loadedState: LoadedState) => void;
  setContextComponentForBlock: (
    loadedStateHash: LoadedStateHash,
    componentIds: ArdoqId[]
  ) => void;
  resetComponentSearchStream: () => void;
  applyFiltersForContextSwitcher: (
    payload: ApplyFiltersForContextSwitcherPayload
  ) => void;
  toggleCollapsedPath: (
    rule: PathCollapsingRule,
    loadedStateHash: LoadedStateHash
  ) => void;
  selectAllForBlock: (
    loadedState: LoadedState,
    componentQuery: QueryBuilderQuery
  ) => void;
};

export const viewpointNavigatorCommands: ViewpointNavigatorCommands = {
  toggleItemVisibility,
  removeLoadedState,
  editBlock,
  setContextComponentForBlock,
  resetComponentSearchStream,
  applyFiltersForContextSwitcher,
  toggleCollapsedPath,
  selectAllForBlock: setStartQueryForLoadedState,
};
